import { normalizeId } from './ids';

/**
 * The doors into Change card (KPE-028, KPR-710). The page itself is
 * `components/changeCard/ChangeCardPage` (KPR-707); this module says how a
 * door builds its address and when a door is shut, and why. Pure, so every
 * entry point — an item's ⋯, a list selection, a filter result and the card
 * editor — asks the same questions and gives the same answers.
 */

/** The API's cap on `ids` for one card change (keepr-api utils/changeCard/dependents.js MAX_IDS). */
export const MAX_CHANGE_CARD_IDS = 1000;

export const CHANGE_CARD_LABEL = 'Change card';

/**
 * `/collections/:id/change-card?card=&ids=a,b` (or `&q=`, or `&all=1`).
 * Exactly one of `ids`, `q`, `all`; anything else is null.
 */
export function changeCardPath(collectionId, { card, ids, q, all } = {}) {
	const cid = normalizeId(collectionId);
	const cardId = normalizeId(card);
	if (!cid || !cardId) return null;
	const idList = (ids || []).map(normalizeId).filter(Boolean);
	const query = typeof q === 'string' ? q.trim() : '';
	if ([idList.length > 0, !!query, !!all].filter(Boolean).length !== 1) return null;
	const params = new URLSearchParams();
	params.set('card', cardId);
	if (idList.length) params.set('ids', idList.join(','));
	else if (query) params.set('q', query);
	else params.set('all', '1');
	// URLSearchParams writes commas as %2C; the page reads either, but a
	// readable address is kinder to someone who copies it.
	return `/collections/${cid}/change-card?${params.toString().replace(/%2C/g, ',')}`;
}

/** One item: shut when the person cannot change it. `reason` is the tooltip. */
export function itemChangeCardState({ canEdit, locked, lockReason, linkedReason } = {}) {
	if (linkedReason) return { disabled: true, reason: linkedReason };
	if (!canEdit) return { disabled: true, reason: 'You cannot edit items of this card.' };
	if (locked) return { disabled: true, reason: lockReason || 'This record is closed to edits.' };
	return { disabled: false, reason: null };
}

/**
 * The list selection. Returns `{ disabled, reason, label, path }`.
 *
 * - picked rows: `ids`, one card, up to the API's cap;
 * - "all N matching": the list filter (`q`), or — with a card and no filter —
 *   every item of that card (`all=1`), which only a manager may do. Rows taken
 *   out of an all-matching selection cannot be named in either, so it is shut.
 */
export function selectionChangeCardState({
	collectionId, count = 0, totalCount = 0, allMatching = false, excludedCount = 0,
	cardId = null, cardFilter = null, feedQuery = '', ids = [], linkedOn = false, review = '', isManager = false,
}) {
	const label = allMatching ? `${CHANGE_CARD_LABEL} for all ${totalCount || count}` : CHANGE_CARD_LABEL;
	const shut = reason => ({ disabled: true, reason, label, path: null });
	if (linkedOn) return shut('Items of other collections cannot change card here.');
	if (review) return shut('Accept the submissions first; waiting ones cannot change card.');
	if (allMatching) {
		if (excludedCount > 0) return shut('Rows were taken out of this selection. Select the items to change instead.');
		if (!cardFilter) return shut('These items use more than one card type. Filter by card type to change card.');
		const query = (feedQuery || '').trim();
		if (!query && !isManager) return shut('Only a manager of this collection can change every item of a card.');
		return { disabled: false, reason: null, label, path: changeCardPath(collectionId, query ? { card: cardFilter, q: query } : { card: cardFilter, all: true }) };
	}
	if (!cardId) return shut('These items use more than one card type. Filter by card type to change card.');
	if (ids.length > MAX_CHANGE_CARD_IDS) {
		return shut(`Change up to ${MAX_CHANGE_CARD_IDS} items at a time. To change more, filter and select all matching.`);
	}
	return { disabled: false, reason: null, label, path: changeCardPath(collectionId, { card: cardId, ids }) };
}

/** The card editor: every item of the card, a manager's act. Null when it is not offered. */
export function cardEditorChangeCardPath(collectionId, cardId, isManager) {
	return isManager ? changeCardPath(collectionId, { card: cardId, all: true }) : null;
}
